import React, { useEffect, useRef } from "react";
import { drawPowerUpToken, POWERUP_VARIANTS } from "../../game-canvas/powerup-designs";
import { getPowerUpImage } from "../../game-canvas/renderers/powerup-image-loader.js";

// Dev-only gallery to compare power-up TOKEN designs (the pickup you drive over). Same
// icon glyphs as the game, everything else varied. Each tile shows one design across a
// representative row: two self buffs (green), two rival debuffs (red), one all (blue),
// plus the same row at a smaller in-game-ish size. Animates on a loop. Route: /pu
const SAMPLE: { name: string; affects: string }[] = [
  { name: "speed", affects: "self" },
  { name: "slim", affects: "self" },
  { name: "slow", affects: "others" },
  { name: "drunk", affects: "others" },
  { name: "switcharoonie", affects: "all" },
];

const SIZES = [22, 13]; // token radius in px: gallery-big, and closer to in-game
const TILE_W = 300;
const TILE_H = 128;

export default function PowerUpDesignsDemo() {
  const canvasRefs = useRef<(HTMLCanvasElement | null)[]>([]);
  const rafRef = useRef<number>(0);
  const startRef = useRef<number>(0);

  useEffect(() => {
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    canvasRefs.current.forEach((c) => { if (c) { c.width = TILE_W * dpr; c.height = TILE_H * dpr; } });

    function loop(now: number) {
      if (!startRef.current) startRef.current = now;
      const t = (now - startRef.current) / 1000;

      POWERUP_VARIANTS.forEach((v, idx) => {
        const c = canvasRefs.current[idx];
        const ctx = c && c.getContext("2d");
        if (!ctx) return;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, TILE_W, TILE_H);
        SIZES.forEach((r, row) => {
          const cy = row === 0 ? 44 : 96;
          const gap = TILE_W / (SAMPLE.length + 1);
          SAMPLE.forEach((s, i) => {
            const cx = gap * (i + 1);
            drawPowerUpToken(ctx, cx, cy, r, getPowerUpImage(s.name), s.affects, v.n, t);
          });
        });
      });
      rafRef.current = window.requestAnimationFrame(loop);
    }
    rafRef.current = window.requestAnimationFrame(loop);
    return () => window.cancelAnimationFrame(rafRef.current);
  }, []);

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto", padding: "8px 16px 60px", fontFamily: "'Rajdhani', sans-serif", color: "#eaf0ff" }}>
      <h1 style={{ fontFamily: "'Orbitron', sans-serif", letterSpacing: 2, fontSize: 22 }}>Power-up token designs</h1>
      <p style={{ color: "#8995b3", fontSize: 14, marginTop: 4 }}>
        The pickup you drive over, same icons as today, everything else up for grabs. Green = a buff on
        you, red = a debuff you drop on rivals, blue = hits everyone. Each tile shows one design at a big
        size (top) and closer to in-game (bottom). Switch it live in a real match with <b>?pu=1..63</b>.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 14, marginTop: 16 }}>
        {POWERUP_VARIANTS.map((v, idx) => (
          <div key={v.n} style={{ background: "#0b0f17", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, padding: "8px 11px" }}>
              <span style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, color: "#37c8e0", fontSize: 15 }}>{v.n}</span>
              <span style={{ fontSize: 12.5, fontWeight: 600, color: "#c3ccdf" }}>{v.label}</span>
            </div>
            <canvas
              ref={(el) => { canvasRefs.current[idx] = el; }}
              style={{ width: TILE_W, height: TILE_H, display: "block", background: "radial-gradient(120% 100% at 50% 40%, #131b2e 0%, #0a0e16 100%)" }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
